import React from "react";
import {
  createBulkOfferAction,
  type CreateBulkOfferActionData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { throwIfFormServiceError } from "src/services/error";
import type { BulkActionType } from "../activity/labels";
import type { BulkTargets } from "../types";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction, trackFormActionError } from "./trackFormAction";
import { useBulkActionProgressToast } from "./useBulkActionProgressToast";
import { useRequireBulkTargets } from "./useRequireBulkTargets";

export type BulkOfferActionType = NonNullable<
  CreateBulkOfferActionData["body"]
>["actionType"];

type EnqueueBulkOfferArgs = {
  actionType: BulkOfferActionType;
  actionData?: { program_id: string | null };
};

function createBulkOfferBody(
  actionType: BulkOfferActionType,
  targets: BulkTargets,
  actionData?: { program_id: string | null }
): CreateBulkOfferActionData["body"] {
  switch (actionType) {
    case "MAKE_OFFER":
      return {
        ...targets,
        actionType: "MAKE_OFFER",
        actionData: actionData ?? { program_id: null },
      };
    case "ACCEPT_OFFER":
      return { ...targets, actionType: "ACCEPT_OFFER" };
    case "DECLINE_OFFER":
      return { ...targets, actionType: "DECLINE_OFFER" };
    case "REVOKE_OFFER":
      return { ...targets, actionType: "REVOKE_OFFER" };
  }
}

export function useEnqueueBulkOffer() {
  const toast = useAvelaToast();
  const showProgressToast = useBulkActionProgressToast();
  const { clearSelection, selectionSize } = useBulkActionTargets();
  const requireTargets = useRequireBulkTargets();

  return React.useCallback(
    async ({
      actionType,
      actionData,
    }: EnqueueBulkOfferArgs): Promise<boolean> => {
      try {
        const targets = await requireTargets();
        if (!targets) {
          return false;
        }

        const response = throwIfFormServiceError(
          await createBulkOfferAction({
            body: createBulkOfferBody(actionType, targets, actionData),
          })
        );

        const trackedActionType = actionType as BulkActionType;
        trackFormAction({
          verb: "Queued",
          actionType: trackedActionType,
          bulk: true,
          count: selectionSize,
          async: true,
          extra:
            actionType === "MAKE_OFFER"
              ? { program_id: actionData?.program_id ?? null }
              : undefined,
        });

        showProgressToast({
          bulkActionId: response.data.bulkActionId,
          formTemplateId: targets.formTemplateId,
          actionType: trackedActionType,
          selectionSize,
        });
        clearSelection();
        return true;
      } catch (err) {
        trackFormActionError(err, {
          actionType: actionType as BulkActionType,
          bulk: true,
          count: selectionSize,
          async: true,
        });
        toast.error({
          title: "Oops! Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
        return false;
      }
    },
    [clearSelection, requireTargets, selectionSize, showProgressToast, toast]
  );
}
